// Session & activity — per-user control over how long a signed-in session
// lives. Saved to auth-service user preferences (userMetadata.session) so the
// choice follows the user across KaziPeople apps; mirrored to the local
// keep-alive cache so it takes effect immediately in this tab.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '@/lib/apiClient';
import {
  DEFAULT_SESSION_PREFS,
  IDLE_TIMEOUT_CHOICES,
  normalizeSessionPrefs,
  setSessionPrefs,
  type SessionPrefs,
} from '@/lib/sessionKeepAlive';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Label } from '@/features/shared/components/ui/label';
import { Switch } from '@/features/shared/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/features/shared/components/ui/select';
import { getApiErrorMessage } from '@/lib/apiError';

export function SessionCard() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<SessionPrefs>({ ...DEFAULT_SESSION_PREFS });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/api/v1/users/profile')
      .then((res) => {
        if (cancelled) return;
        const p = normalizeSessionPrefs(res.data?.userMetadata?.session);
        setPrefs(p);
        setSessionPrefs(p);
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (next: SessionPrefs) => {
    const prev = prefs;
    setPrefs(next);
    setSessionPrefs(next); // effective immediately for the keep-alive loop
    try {
      await apiClient.put('/api/v1/users/profile/preferences', { session: next });
      toast.success(t('settings:session_saved', 'Session preference saved'));
    } catch (e) {
      setPrefs(prev);
      setSessionPrefs(prev);
      toast.error(getApiErrorMessage(e, t('settings:session_save_failed', 'Could not save the session preference')));
    }
  };

  const idleLabel = (m: number) =>
    m === 0
      ? t('settings:session_idle_never', 'Never — stay signed in')
      : m < 60
        ? t('settings:session_idle_minutes', '{{count}} minutes of inactivity', { count: m })
        : t('settings:session_idle_hours', '{{count}} hour(s) of inactivity', { count: m / 60 });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" /> {t('settings:session_title', 'Session & Activity')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 max-w-xl">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="keep-active">{t('settings:session_keep_active', 'Stay signed in while I’m active')}</Label>
            <p className="text-xs text-muted-foreground">
              {t(
                'settings:session_keep_active_hint',
                'Quietly renews your session while you’re using the app, so you aren’t signed out mid-task.',
              )}
            </p>
          </div>
          <Switch
            id="keep-active"
            disabled={!loaded}
            checked={prefs.keepActive}
            onCheckedChange={(v) => save({ ...prefs, keepActive: v })}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('settings:session_idle_label', 'Automatically sign me out after')}</Label>
          <Select
            disabled={!loaded}
            value={String(prefs.idleTimeoutMinutes)}
            onValueChange={(v) => save({ ...prefs, idleTimeoutMinutes: Number(v) })}
          >
            <SelectTrigger className="w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IDLE_TIMEOUT_CHOICES.map((m) => (
                <SelectItem key={m} value={String(m)}>
                  {idleLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t(
              'settings:session_idle_hint',
              'A shared or public computer? Pick a short timeout. Your session still ends when you sign out or your organization’s security policy requires it.',
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
